import React, {useContext, useEffect, useRef, useState} from "react";
import {
  createMarkdownResource,
  deleteMarkdownResource,
  updateMarkdownResource,
  updateMarkdownResourceFile
} from "requests/cms";
import {MCard, MDropzone} from "components/Shared";
import {KeyValueOriginal, LabelValue, MCardField} from "components/Shared/MCard";
import {MarkdownResourceRequest, MarkdownResourceResponse} from "requests/cms/interfaces";
import IconButton from "components/Shared/IconButton";
import {stopPropagationPreventDefault} from "tools/clickEvents";
import {DialogContext, DialogProviderValue} from "components/Shared/DialogProvider";
import {getUniqueKey} from "./ContentPane";
import * as yup from "yup";
import TextField from "react-md/lib/TextFields/TextField";
import {DATA_TYPE_DROPDOWN2} from "../../typeConstants/dataTypes";

interface Props {
  resources: MarkdownResourceResponse[],
  refreshResources: () => void,
};

interface resultScore{
    value: MarkdownResourceResponse;
    score: number;
}

const ResourceManager = ({ resources, refreshResources }: Props) => {
  const mounted = useRef(true);
  const dialogContext = useContext<DialogProviderValue>(DialogContext);
  const [isLoading, setIsLoading] = useState(false); // when this is set back to false, we will reload resources
  const [resourceFilter, setResourceFilter] = useState<string>("");
  const [resourceFiltered, setResourceFiltered] = useState<MarkdownResourceResponse[]>(resources);
  const resourceCategories = useRef<LabelValue[]>([]);

  const onFileAdd = (files: { file: File }[]) => {
    setIsLoading(true);
    files.map(({ file }) => {
      let reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onloadstart = (evt) => {
        // loading files
      };
      reader.onloadend = (evt) => {
        if (evt.target.readyState == FileReader.DONE) {
          let convertedfile = evt.target.result;
          showFileUploadDialog(convertedfile as ArrayBuffer, file.type);
        }
      };
      reader.onerror = () => setIsLoading(false);
    });
  };

  const escapeRegExp = (input: string): string => input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const filterResources = () => {
    const dateCompare = (a: Date, b: Date) => (new Date(b).getTime() - new Date(a).getTime());

    const filterSort = (a: resultScore, b: resultScore) => {
      if (a.score === b.score) {
        return dateCompare(a.value.modifiedDate, b.value.modifiedDate);
      } else {
        return b.score - a.score;
      }
    };

    if (resourceFilter && resourceFilter.replace(" ", "").length > 0) {
      const lowerFilterParts = resourceFilter.split(" ").filter(s => s.length > 0).map(escapeRegExp).join("|");
      const filterRegEx = new RegExp(lowerFilterParts, "ig");
      const resultScoring: resultScore[] = resources.map(r => {
                const item: resultScore = {
                  value: r,
                  score: (
                          ((r.name ?? "").match(filterRegEx)?.length ?? 0) +
                          ((r.category ?? "").match(filterRegEx)?.length ?? 0) +
                          ((r.description ?? "").match(filterRegEx)?.length ?? 0) +
                          ((r.uniqueKey ?? "").match(filterRegEx)?.length ?? 0)
                  )
                };
                return item;
              }
      ).filter(r => r.score > 0).sort(filterSort);
      const result = resultScoring.map(r => r.value);
      setResourceFiltered(result);
    } else {
      setResourceFiltered(resources);
    }
  };

  const addFileToResource = (resourceId: string, file: ArrayBuffer, fileType: string) => updateMarkdownResourceFile(resourceId, file, fileType, () => setIsLoading(false), mounted);

  const addResource = (resource: MarkdownResourceRequest, file: ArrayBuffer, fileType: string) => {
    if (!resource.uniqueKey) resource.uniqueKey = getUniqueKey(resource.name);
    createMarkdownResource(resource, resource => {
      addFileToResource(resource.markdownResourceId, file, fileType);
      setResourceFilter(resource.category);
    }, mounted);
  };

  const getFields = (resource: MarkdownResourceRequest, resourceCategories: LabelValue[]): MCardField[] => [
    {
      key: "name",
      label: "Name",
      value: resource.name,
      validation: yup.string().required("Name is required"),
    },
    {
      key: "category",
      label: "Category",
      value: resource.category,
      dataType: DATA_TYPE_DROPDOWN2,
      dataTypeOptions: {
        creatable: true,
        options: resourceCategories
      },
      validation: yup.string().required("Category is required"),
    },
    {
      key: "description",
      label: "Description",
      value: resource.description,
      validation: yup.string().required("Description is required"),
    },
  ];

  const showFileUploadDialog = (file: ArrayBuffer, fileType: string) => {
    let resource = { } as MarkdownResourceRequest;
    let controlMethodsRef = { value: null };

    const handleFieldChange = (changeArray: KeyValueOriginal[], changeMap: Record<string, string>) => {
      changeArray.forEach(c => resource[c.key] = c.value);
    };

    const onUploadClicked = () => {
      if (controlMethodsRef.value.runValidation(true)) {
        dialogContext.hide();
        addResource(resource, file, fileType);
      } else {
        return false;
      }
    };

    const fields = getFields(resource, resourceCategories.current);

    dialogContext.show(null,
      <MCard
        title="Upload Resource"
        fields={fields}
        onlyEditable
        onEditChanged={handleFieldChange}
        getControlMethods={cm => controlMethodsRef.value = cm}
      />, [
        { label: "Upload", isPrimary: true, onClick: onUploadClicked,  },
        { label: "Cancel", isPrimary: false, onClick: dialogContext.hide },
      ], { className: "resource-upload-dialog" });
  };

  const onEditResource = (resource: MarkdownResourceResponse) => {
    let controlMethodsRef = { value: null };
    const handleFieldChange = (changeArray: KeyValueOriginal[], changeMap: Record<string, string>) => {
      changeArray.forEach(c => resource[c.key] = c.value);
    };

    const fields = getFields(resource, resourceCategories.current);

    function onEditResourceSave() {
      if (controlMethodsRef.value.runValidation(true)) {
        setIsLoading(true);
        updateMarkdownResource(resource.markdownResourceId, resource, () => {
          dialogContext.hide();
          setIsLoading(false);
        }, mounted);
      } else {
        return false;
      }
    }

    dialogContext.show(null,
            <MCard
                    title="Edit Resource"
                    fields={fields}
                    onlyEditable
                    onEditChanged={handleFieldChange}
                    getControlMethods={cm => controlMethodsRef.value = cm}
            />, [
              { label: "Save", isPrimary: true, onClick: onEditResourceSave,  },
              { label: "Cancel", isPrimary: false, onClick: dialogContext.hide },
            ], { className: "resource-upload-dialog" });
  };

  const viewResource = (resource: MarkdownResourceResponse) => window.open(resource.url, "#blank");

  const onFileDelete = (resource: MarkdownResourceResponse, areYouSure = false) => {
    if (areYouSure === true) {
      setIsLoading(true);
      deleteMarkdownResource(resource.markdownResourceId, (resp) => {
        if (resp.error){
          const messageLines = resp.message
                  .split('\n')
                  .map((line, i) => <>{line}{i === 0 ? <br/> : <></>}<br/></>);
          dialogContext.showOk("Resource Not Deleted", messageLines);
        }
      }, mounted)
              .finally(() => setIsLoading(false));
    } else {
      dialogContext.showYesNo("Deleting File", `Are you sure you want to delete ${resource.name}?`, () => onFileDelete(resource, true));
    }
  };

  const mapResourceToFile = (resource: MarkdownResourceResponse) => ({
    ...resource,
    fileName: `${resource.name}.${resource.fileExtension}`,
    lastModified: resource.createDate,
    downloadUrl: resource.url,
    viewUrl: resource.url,
    fileSizeBytes: resource.fileSizeBytes,
    customActions: <div className="resource-actions">
      <IconButton icon="content_copy" tooltip="Copy Resource Markdown" onClick={stopPropagationPreventDefault(() => navigator.clipboard.writeText(`@${resource.uniqueKey}`))} />
      <IconButton icon="visibility" tooltip="View Resource" onClick={stopPropagationPreventDefault(() => viewResource(resource))} />
      <IconButton icon="edit" tooltip="Edit Resource" onClick={stopPropagationPreventDefault(() => onEditResource(resource))} />
      <IconButton icon="delete_forever" tooltip="Delete Resource" onClick={stopPropagationPreventDefault(() => onFileDelete(resource))} />
    </div>,
  });

  useEffect(() => {
    // Filter resources based on resourceFilter with debounce
    const timeout = setTimeout(filterResources, 750);
    return () => clearTimeout(timeout);
  }, [resourceFilter]);

  useEffect(() => {
    if (!resources) return;
    filterResources();
    resourceCategories.current = resources.map(r => r.category)
            .filter((v, i, a) => a.indexOf(v) === i)
            .sort((a, b) => a.localeCompare(b))
            .map(c => ({ label: c, value: c }));
  }, [resources]);

  useEffect(() => {
    if (!isLoading) {
      refreshResources();
    }
  }, [isLoading]);

  return (
    /* @ts-ignore MDropzone props seem off with typescript but idk why? */
    <div className="resource-manager-pane">
      <TextField placeholder="Filter: Search for category or other keyword" value={resourceFilter}
                 onChange={(v, e) => {
                   setResourceFilter(v.toString());
                   e.stopPropagation();
                 }} />
      <MDropzone
        validFileTypes={["image/*", "application/pdf"]}
        existingFiles={resourceFiltered?.map(mapResourceToFile)}
        onFileAdd={onFileAdd}
        onFileRemove={onFileDelete}
        prompt="Click to upload content"
        columnDefinitionOverride={[
          { name: "Name", dataSource: "name", className: "file-name-cell" },
          { name: "Category", dataSource: "category" },
          { name: "Description", dataSource: "description" },
          { name: "Key", dataSource: "uniqueKey" },
          // { name: "Type", dataSource: "mimeType", style: { width: "50px" } },
          { name: "Actions", dataSource: "customActions", style: { width: "160px" } },
        ]}
      />
    </div>
  );
}

export default ResourceManager;
